/**
 * @Author: claude
 * @Date: 2026/4/17
 * @Description: 全局任务状态悬浮球组件
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Badge, Spin, Empty } from 'antd';
import { SyncOutlined, CloseOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { getRunningBatches } from '@/api/task';

// 任务类型映射
const TASK_TYPE_MAP: Record<number, { label: string; path: string; detailPath?: string }> = {
	8: { label: '文生图', path: '/workspace/batchTools/textToImg/index', detailPath: '/workspace/batchTools/textToImg/detail' },
	9: { label: '相似图裂变', path: '/workspace/batchTools/similar/index', detailPath: '/workspace/batchTools/similar/detail' },
	6: { label: '平铺图生成', path: '/workspace/batchTools/continuous/index', detailPath: '/workspace/batchTools/continuous/detail' },
	7: { label: '四方循环图', path: '/workspace/batchTools/fourSided/index', detailPath: '/workspace/batchTools/fourSided/detail' },
	14: { label: '图案裁剪', path: '/workspace/batchTools/cut/index', detailPath: '/workspace/batchTools/cut/detail' },
	11: { label: '图片去背景', path: '/workspace/batchTools/blockingOut/index', detailPath: '/workspace/batchTools/blockingOut/detail' },
	12: { label: '图片变清晰', path: '/workspace/batchTools/clear/index', detailPath: '/workspace/batchTools/clear/detail' },
	51: { label: '商品套图', path: '/workspace/batchTools/photoSet/index', detailPath: '/workspace/batchTools/photoSet/detail' },
	52: { label: '印花图提取', path: '/workspace/batchTools/extract/index', detailPath: '/workspace/batchTools/extract/detail' },
	22: { label: '图片去水印', path: '/workspace/batchTools/removeWatermark/index', detailPath: '/workspace/batchTools/removeWatermark/detail' },
	50: { label: '裁剪压缩', path: '/workspace/batchTools/trimCompress/index', detailPath: '/workspace/batchTools/trimCompress/detail' },
	17: { label: '侵权风险过滤', path: '/workspace/batchTools/filter/index', detailPath: '/workspace/batchTools/filter/detail' },
	18: { label: '标题提取', path: '/workspace/batchTools/titleExtraction/index', detailPath: '/workspace/batchTools/titleExtraction/detail' },
	60: { label: '转矢量图', path: '/workspace/batchTools/transformVectorDiagram/index', detailPath: '/workspace/batchTools/transformVectorDiagram/detail' },
	100: { label: '香蕉大模型', path: '/workspace/batchTools/uploadImage/index' },
};

// 状态映射
const STATUS_MAP: Record<number, { label: string; color: string }> = {
	3: { label: '执行中', color: 'blue' },
	4: { label: '排队中', color: 'orange' },
	5: { label: '准备中', color: 'purple' },
};

interface RunningBatch {
	batchId: string;
	batchNumber: string;
	type: number;
	typeDesc?: number;
	source: string;
	userId?: number;
	teamId?: number;
	workflowNodeExecutionId?: number;
	workflowId?: string | null;
	workflowName?: string | null;
	status: number;
	statusDesc?: string;
	createTime: string;
	updateTime?: string;
	totalAmount: number;
	successAmount: number;
	failAmount: number;
	remark?: string | null;
	platform?: string | null;
}

interface Statistics {
	queuingCount: number;
	runningCount: number;
	totalBatchCount: number;
	totalFail?: number;
	totalSuccess?: number;
	totalTasks?: number;
}

export const TaskFloatingBall = () => {
	const [expanded, setExpanded] = useState(false);
	const [loading, setLoading] = useState(false);
	const [batchList, setBatchList] = useState<RunningBatch[]>([]);
	const [statistics, setStatistics] = useState<Statistics | null>(null);
	const [minimized, setMinimized] = useState(false);

	// 获取运行中的批次列表
	const fetchRunningBatches = useCallback(async () => {
		setLoading(true);
		try {
			// requestInstance 拦截器在 code=200 时返回 response.data.data
			// 所以这里直接拿到 { list: [...], statistics: {...} }
			const data: any = await getRunningBatches();
			
			console.log('API 返回数据:', data);
			
			if (data && Array.isArray(data.list)) {
				setBatchList(data.list);
				setStatistics(data.statistics || null);
			}
		} catch (error) {
			console.error('获取运行中批次失败:', error);
		} finally {
			setLoading(false);
		}
	}, []);

	// 组件挂载时获取一次数据
	useEffect(() => {
		fetchRunningBatches();
		// 每30秒刷新一次
		const timer = setInterval(fetchRunningBatches, 30000);
		return () => clearInterval(timer);
	}, [fetchRunningBatches]);

	// 计算任务总数
	const totalCount = useMemo(() => {
		if (!statistics) return 0;
		return (statistics.runningCount || 0) + (statistics.queuingCount || 0);
	}, [statistics]);

	// 展开/收起面板
	const toggleExpand = () => {
		setExpanded(!expanded);
	};

	// 最小化
	const handleMinimize = () => {
		setExpanded(false);
		setMinimized(true);
	};

	// 如果没有数据且已最小化，不显示
	if (minimized && totalCount === 0) {
		return null;
	}

	return (
		<>
			{/* 悬浮球 */}
			{!expanded && (
				<div
					className="fixed bottom-6 right-6 z-[9999] cursor-pointer group"
					onClick={toggleExpand}
				>
					<Badge count={totalCount} size="small" offset={[-2, 2]}>
						<div
							className="w-12 h-12 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-all"
							style={{ backgroundColor: '#32649F' }}
						>
							<ClockCircleOutlined className="text-white text-xl" />
						</div>
					</Badge>
					{/* 悬停提示 */}
					<div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
						<div className="bg-white shadow-lg rounded-lg px-3 py-2 whitespace-nowrap text-sm text-gray-600 border border-gray-200">
							<p>运行中: {statistics?.runningCount || 0}</p>
							<p>排队中: {statistics?.queuingCount || 0}</p>
						</div>
					</div>
				</div>
			)}

			{/* 展开面板 */}
			{expanded && (
				<div className="fixed bottom-6 right-6 z-[9999]">
					<div className="bg-white rounded-xl shadow-2xl w-[420px] max-h-[500px] overflow-hidden border border-gray-200">
						{/* 头部 */}
						<div
							className="px-4 py-3 flex items-center justify-between border-b border-gray-100"
							style={{ backgroundColor: '#f8fafc' }}
						>
							<div className="flex items-center gap-3">
								<ClockCircleOutlined className="text-lg" style={{ color: '#32649F' }} />
								<span className="font-medium text-gray-700">任务进度监控</span>
								{loading && <SyncOutlined spin className="text-gray-400 text-sm" />}
							</div>
							<div className="flex items-center gap-2">
								<button
									onClick={fetchRunningBatches}
									className="p-1 hover:bg-gray-100 rounded transition-colors"
									title="刷新"
								>
									<SyncOutlined className="text-gray-500 text-sm" />
								</button>
								<button
									onClick={handleMinimize}
									className="p-1 hover:bg-gray-100 rounded transition-colors"
									title="最小化"
								>
									<CloseOutlined className="text-gray-500 text-sm" />
								</button>
							</div>
						</div>

						{/* 统计信息 */}
						{statistics && (
							<div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-4 text-sm">
								<span className="text-gray-600">
									运行中: <span className="text-blue-500 font-medium">{statistics.runningCount}</span>
								</span>
								<span className="text-gray-600">
									排队中: <span className="text-orange-500 font-medium">{statistics.queuingCount}</span>
								</span>
								<span className="text-gray-600">
									总任务: <span className="text-gray-700 font-medium">{statistics.totalBatchCount}</span>
								</span>
							</div>
						)}

						{/* 列表内容 */}
						<div className="max-h-[350px] overflow-y-auto">
							{loading && batchList.length === 0 ? (
								<div className="flex items-center justify-center py-12">
									<Spin size="default" />
								</div>
							) : batchList.length === 0 ? (
								<div className="py-12">
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description={
											<span className="text-gray-400">暂无运行中的任务</span>
										}
									/>
								</div>
							) : (
								<div className="divide-y divide-gray-50">
									{batchList.map((item) => {
										const typeInfo = TASK_TYPE_MAP[item.type] || TASK_TYPE_MAP[100];
										const statusInfo = STATUS_MAP[item.status] || { label: item.statusDesc || '未知', color: 'default' };
										const progress = item.totalAmount > 0
											? Math.round((item.successAmount / item.totalAmount) * 100)
											: 0;

										return (
											<div
												key={item.batchId}
												className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
											>
												<div className="flex items-start justify-between gap-2">
													<div className="flex-1 min-w-0">
														<div className="flex items-center gap-2 mb-1">
															<span
																className="px-2 py-0.5 text-xs rounded"
																style={{ backgroundColor: 'rgba(50, 100, 159, 0.1)', color: '#32649F' }}
															>
																{typeInfo.label}
															</span>
															<span
																className={`px-2 py-0.5 text-xs rounded ${
																	statusInfo.color === 'blue'
																		? 'bg-blue-100 text-blue-600'
																		: statusInfo.color === 'orange'
																		? 'bg-orange-100 text-orange-600'
																		: 'bg-gray-100 text-gray-600'
																}`}
															>
																{statusInfo.label}
															</span>
														</div>
														<p className="text-xs text-gray-400 truncate">
															批次号: {item.batchNumber}
														</p>
														{item.remark && (
															<p className="text-xs text-gray-400 truncate mt-0.5">
																备注: {item.remark}
															</p>
														)}
													</div>
													<div className="text-right flex-shrink-0">
														<div className="text-sm font-medium text-gray-700">
															{item.successAmount}
															<span className="text-gray-400">/{item.totalAmount}</span>
														</div>
														<div className="text-xs text-gray-400">
															{progress}%
														</div>
													</div>
												</div>
												{/* 进度条 */}
												<div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
													<div
														className="h-full transition-all duration-300"
														style={{ width: `${progress}%`, backgroundColor: '#32649F' }}
													/>
												</div>
											</div>
										);
									})}
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default TaskFloatingBall;
